/**
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * Docusaurus project root. Adapted from Docusaurus 3.10.2.
 */
// Extensão pontual do dropdown do Docusaurus 3.10.2: Escape fecha o
// painel, retorna o foco e mantém aria-expanded coerente com a abertura.
import React, { useEffect, useId, useRef, useState } from 'react';
import clsx from 'clsx';
import NavbarNavLink from '@theme/NavbarItem/NavbarNavLink';
import NavbarItem from '@theme/NavbarItem';

export default function DropdownNavbarItemDesktop({ items, position, className, onClick, ...props }) {
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuId = useId();
  const [showDropdown, setShowDropdown] = useState(false);
  const [dismissedByEscape, setDismissedByEscape] = useState(false);

  useEffect(() => {
    const handleOutside = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setShowDropdown(false);
        setDismissedByEscape(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('focusin', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('focusin', handleOutside);
    };
  }, []);

  const handleEscape = (event) => {
    if (event.key !== 'Escape' || (!showDropdown && !dropdownRef.current?.matches(':hover'))) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setShowDropdown(false);
    setDismissedByEscape(true);
    triggerRef.current?.focus();
  };

  return (
    <div
      ref={dropdownRef}
      className={clsx('navbar__item', 'dropdown', 'dropdown--hoverable', {
        'dropdown--right': position === 'right',
        'dropdown--show': showDropdown,
        'dropdown--escape-dismissed': dismissedByEscape,
      })}
      onMouseLeave={() => setDismissedByEscape(false)}
      onKeyDown={handleEscape}>
      <NavbarNavLink
        ref={triggerRef}
        aria-haspopup="true"
        aria-expanded={showDropdown}
        aria-controls={menuId}
        role="button"
        href={props.to ? undefined : '#'}
        className={clsx('navbar__link', className)}
        {...props}
        onClick={props.to ? undefined : (event) => event.preventDefault()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setDismissedByEscape(false);
            setShowDropdown((current) => !current);
          }
        }}>
        {props.children ?? props.label}
      </NavbarNavLink>
      <ul id={menuId} className="dropdown__menu">
        {items.map((item, index) => (
          <NavbarItem isDropdownItem activeClassName="dropdown__link--active" {...item} key={index} />
        ))}
      </ul>
    </div>
  );
}
